import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import CreditCardIcon from '../../assets/icons/svg/CreditCardIcon.svg'
import { CreditCardConfigs } from './CreditCardConfigs'
import InputBox from '../forms/inputbox/InputBox'
import { useTranslate } from '@repo/unicity'
import { Datepicker, setOptions, type MbscDatepickerChangeEvent } from '@mobiscroll/react'

import { useAtom, useAtomValue } from 'jotai'
import { creditCardState } from '../store/creditCard'
import type { TCreditCard } from '../../types/TCreditCard'
import CreditCardNumber from './CreditCardNumber'
import { useEffect, useMemo } from 'react'

import './style.css'
import { countryState } from '@repo/unicity'
import { CreditCardHelper } from './CreditCardHelper'

type TPaymentItem = {
    item: {
        name: string
        key: string
        icon: React.ReactNode
        subName?: React.ReactNode
    }
    isChecked: boolean
    children?: React.ReactNode
    sx?: React.CSSProperties
    onClick?: () => void
}

setOptions({
    theme: 'ios',
    themeVariant: 'light'
})

type TCreditCardExpireProps = {
    value: {
        month: string
        year: string
    }
    onSet: (month: string, year: string) => void
    label: string
    name: string
    disabled: boolean
}
const CreditCardBox = ({
    RowItem,
    isChecked,
    onClick,
    isLoading
}: {
    RowItem: React.ComponentType<TPaymentItem>
    isChecked: boolean
    onClick: () => void
    isLoading: boolean
}) => {
    const { translate } = useTranslate()
    const [creditCard, setCreditCard] = useAtom(creditCardState)
    const country = useAtomValue(countryState)

    useEffect(() => {
        return () => {
            setCreditCard({
                creditCardNumber: '',
                expDate: '',
                cvv: '',
                payer: ''
            })
        }
    }, [])

    const CreditCardIconsRow = useMemo(() => {
        return (
            <Stack direction={'row'} gap={'8px'}>
                {country.payment_options.map(option => {
                    return <img src={CreditCardHelper.BrandOfCard(option)} alt={option} style={{ height: '16px' }} />
                })}
            </Stack>
        )
    }, [])

    return (
        <Stack>
            <RowItem
                item={{
                    name: translate('credit_card'),
                    key: 'credit_card',
                    subName: CreditCardIconsRow,
                    icon: <img src={CreditCardIcon} alt="CreditCardIcon" />
                }}
                isChecked={isChecked}
                onClick={onClick}
                sx={{ backgroundColor: 'rgba(244, 248, 251, 1)' }}>
                <Stack
                    padding="0px"
                    paddingLeft={{
                        xs: '0px',
                        md: '46px'
                    }}>
                    <Divider sx={{ borderColor: 'rgba(204, 222, 239, 1)', margin: '24px 0px' }} />
                    <Stack
                        direction={'column'}
                        gap={{
                            xs: '16px',
                            md: '24px'
                        }}
                        width={'100%'}
                        display={{
                            xs: 'flex',
                            md: 'grid'
                        }}
                        pb={'16px'}
                        gridTemplateColumns={'1fr 1fr'}>
                        {CreditCardConfigs.Form.map(item => {
                            return (
                                <Stack direction={'row'} gap={'16px'} key={item.key} width={'100%'}>
                                    {['tel', 'text', 'password'].includes(item.type) && (
                                        <InputBox
                                            label={translate(item.label)}
                                            // onKeyUp={checkDisabled}
                                            name={item.key}
                                            type={item.type}
                                            placeholder={translate(item.key) || ''}
                                            value={creditCard[item.key as keyof TCreditCard]}
                                            onChange={(value: string) => {
                                                if (item.key === 'cvv' && !/^\d{0,4}$/.test(value)) return
                                                setCreditCard(prev => ({
                                                    ...prev,
                                                    [item.key]: value
                                                }))
                                            }}
                                            sx={{
                                                width: '100%'
                                            }}
                                            disabled={isLoading}
                                        />
                                    )}
                                    {item.type === 'ExpDate' && (
                                        <CreditCardExpire
                                            value={{ month: creditCard.expDate.split('-')[1] || '', year: creditCard.expDate.split('-')[0] || '' }}
                                            onSet={(month, year) => {
                                                setCreditCard(prev => ({
                                                    ...prev,
                                                    expDate: `${year}-${month}`
                                                }))
                                            }}
                                            label={translate('exp_date')}
                                            name={item.key}
                                            disabled={isLoading}
                                        />
                                    )}
                                    {item.type === 'CreditCardNumber' && (
                                        <CreditCardNumber label={translate(item.label)} name={item.key} disabled={isLoading} />
                                    )}
                                </Stack>
                            )
                        })}
                    </Stack>
                </Stack>
            </RowItem>
        </Stack>
    )
}

const CreditCardExpire = ({ value, onSet, label, name, disabled }: TCreditCardExpireProps) => {
    const { translate } = useTranslate()
    const now = new Date()
    const until = new Date(now.getFullYear() + 10, now.getMonth())

    const onSetDate = (event: MbscDatepickerChangeEvent) => {
        const splitDate = event.valueText?.split('/')
        if (splitDate && splitDate[0] && splitDate[1]) {
            onSet(splitDate[0], splitDate[1])
        }
    }

    return (
        <Stack width={'100%'}>
            <Stack sx={{ position: 'relative' }}>
                <Datepicker
                    theme="ios"
                    themeVariant="light"
                    display="center"
                    touchUi={true}
                    monthText="MM"
                    yearText="YYYY"
                    controls={['date']}
                    dateWheels={'MM YYYY'}
                    dateFormat={'MM/YYYY'}
                    min={now}
                    max={until}
                    setText={translate('ok')}
                    cancelText={translate('cancel')}
                    onChange={onSetDate}
                    value={`${value.month.length > 0 && value.year.length > 0 ? `${value.month}/${value.year}` : ''}`}
                    label={label}
                    name={name}
                    labelStyle="floating"
                    disabled={disabled}
                />
            </Stack>
        </Stack>
    )
}

export default CreditCardBox
